import { useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';

/**
 * Custom hook to extract common slot IDs from URL params and query string
 * Usage: const { treatmentSlotId, solutionSlotId, serviceSlotId, patientPlanId, id } = useSlotIds();
 */
export const useSlotIds = () => {
    // Get URL parameters from react-router
    const params = useParams();
    const location = useLocation();

    // Memoize the extraction logic to prevent unnecessary re-runs
    return useMemo(() => {
        const urlParams = new URLSearchParams(location.search);

        const treatmentSlotId = urlParams.get('slotId') || urlParams.get('treatmentSlotId');
        const solutionSlotId = urlParams.get('solutionslotId') || urlParams.get('solutionSlotId');
        const serviceSlotId = urlParams.get('serviceSlotId');
        const programSlotId = urlParams.get('programSlotId');
        const roomName = urlParams.get('roomName');
        const roomType = urlParams.get('roomType');
        let activeSlotId = null;
        let activeSlotName = '';

        if (programSlotId) {
            activeSlotId = programSlotId;
            activeSlotName = 'program';
        } else if (treatmentSlotId) {
            activeSlotId = treatmentSlotId;
            activeSlotName = 'treatment';
        } else if (solutionSlotId) {
            activeSlotId = solutionSlotId;
            activeSlotName = 'solution';
        } else if (serviceSlotId) {
            activeSlotId = serviceSlotId;
            activeSlotName = 'service';
        }

        return {
            treatmentSlotId,
            solutionSlotId,
            serviceSlotId,
            programSlotId,
            roomName,
            roomType,
            patientPlanId: params.patientPlanId,
            id: params.id, // Generic ID often used for UserID or specific record ID
            activeSlotId,
            activeSlotName
        };
    }, [params, location.search]);
};
